<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <header class="layout-topbar">
    <div class="topbar-start">
      <!-- Menu Toggle Button -->
      <button
        v-if="showMenuButton"
        class="topbar-menu-button layout-topbar-menu-button"
        @click="$emit('toggle-menu')"
      >
        <i class="ri-menu-line"></i>
      </button>

      <!-- Logo (shown in horizontal mode or when sidebar is hidden) -->
      <slot v-if="horizontalMenu || showLogo" name="logo">
        <router-link to="/" class="topbar-logo">
          <img
            v-if="logo"
            :src="logo"
            :alt="logoAlt"
            class="topbar-logo-image"
          />
        </router-link>
      </slot>
    </div>

    <!-- Horizontal Menu (when in horizontal mode) -->
    <nav v-if="horizontalMenu" class="topbar-menu">
      <template v-for="item in menuItems" :key="item.labelKey || item.label">
        <!-- Item with subitems (dropdown) -->
        <div
          v-if="item.items && item.items.length > 0"
          class="topbar-menu-dropdown"
        >
          <button
            class="topbar-menu-item"
            :class="{ active: hasActiveChild(item) }"
            @click="toggleDropdown($event, item)"
          >
            <i :class="item.icon"></i>
            <span>{{ getLabel(item) }}</span>
            <i class="ri-arrow-down-s-line dropdown-arrow"></i>
          </button>
          <PrimeMenu
            :ref="'dropdown-' + (item.labelKey || item.label)"
            :model="getDropdownItems(item)"
            :popup="true"
          />
        </div>
        <!-- Simple item -->
        <router-link
          v-else
          :to="item.to"
          class="topbar-menu-item"
          :class="{ active: $route.path === item.to }"
        >
          <i :class="item.icon"></i>
          <span>{{ getLabel(item) }}</span>
        </router-link>
      </template>
    </nav>

    <div class="topbar-end">
      <!-- Custom actions slot (before default actions) -->
      <slot name="actions-start"></slot>

      <!-- Search (optional) -->
      <router-link
        v-if="showSearch"
        :to="searchRoute"
        class="topbar-action"
        v-tooltip.bottom="searchTooltip"
      >
        <i class="ri-search-line"></i>
      </router-link>

      <!-- Portal link (optional) -->
      <a
        v-if="portalUrl"
        :href="portalUrl"
        class="topbar-action"
        v-tooltip.bottom="portalTooltip"
      >
        <i class="ri-home-smile-line"></i>
      </a>

      <!-- Settings dropdown (includes logout on mobile) -->
      <template
        v-if="computedSettingsItems && computedSettingsItems.length > 0"
      >
        <PrimeMenu
          ref="settingsMenu"
          :model="computedSettingsItems"
          :popup="true"
        />
        <button
          class="topbar-action"
          @click="toggleSettingsMenu"
          v-tooltip.bottom="settingsTooltip"
        >
          <i class="ri-settings-3-line"></i>
        </button>
      </template>

      <!-- Custom actions slot (after default actions) -->
      <slot name="actions-end"></slot>

      <!-- Logout (hidden on mobile, included in settings menu instead) -->
      <button
        v-if="showLogout && !isMobile"
        class="topbar-action"
        v-tooltip.bottom="logoutTooltip"
        @click="handleLogout"
      >
        <i class="ri-logout-box-line"></i>
      </button>
    </div>
  </header>
</template>

<script>
import { computed } from "vue";
import Menu from "primevue/menu";
import { useLayout } from "@/composables/useLayout";
import { useAuth } from "@/composables/useAuth";

/**
 * Check if a section contains the active route
 * @param {Object} section - Menu section
 * @param {string} currentPath - Current route path
 * @returns {boolean}
 */
function sectionContainsActivePath(section, currentPath) {
  if (section.to === currentPath) {
    return true;
  }
  if (section.items) {
    return section.items.some((item) => item.to === currentPath);
  }
  return false;
}

export default {
  name: "AppTopbar",
  components: {
    PrimeMenu: Menu,
  },
  props: {
    /**
     * Show the menu toggle button
     */
    showMenuButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable horizontal menu mode
     */
    horizontalMenu: {
      type: Boolean,
      default: false,
    },
    /**
     * Show logo in topbar
     */
    showLogo: {
      type: Boolean,
      default: false,
    },
    /**
     * Logo image path. Can be overridden with #logo slot.
     */
    logo: {
      type: String,
      default: "",
    },
    /**
     * Logo alt text
     */
    logoAlt: {
      type: String,
      default: "Pristy",
    },
    /**
     * Menu items for horizontal mode
     * Each item: { label, labelKey, icon, to, items }
     */
    menuItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Show search button
     */
    showSearch: {
      type: Boolean,
      default: true,
    },
    /**
     * Search route path
     */
    searchRoute: {
      type: String,
      default: "/search",
    },
    /**
     * Search button tooltip
     */
    searchTooltip: {
      type: String,
      default: "Recherche",
    },
    /**
     * Portal URL (external link). Set to empty to hide.
     */
    portalUrl: {
      type: String,
      default: "",
    },
    /**
     * Portal button tooltip
     */
    portalTooltip: {
      type: String,
      default: "Portail",
    },
    /**
     * Settings menu items array
     * Each item: { label, icon, command }
     */
    settingsItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Settings button tooltip
     */
    settingsTooltip: {
      type: String,
      default: "Paramètres",
    },
    /**
     * Show logout button
     */
    showLogout: {
      type: Boolean,
      default: true,
    },
    /**
     * Logout button tooltip
     */
    logoutTooltip: {
      type: String,
      default: "Déconnexion",
    },
    /**
     * Login route name for redirect after logout
     */
    loginRouteName: {
      type: String,
      default: "login",
    },
  },
  emits: ["toggle-menu", "logout"],
  setup(props) {
    const { isMobile } = useLayout();
    const { handleLogout: doLogout } = useAuth();

    // On mobile, add logout to settings menu
    const computedSettingsItems = computed(() => {
      const items = [...(props.settingsItems || [])];

      if (isMobile.value && props.showLogout) {
        // Add separator if there are existing items
        if (items.length > 0) {
          items.push({ separator: true });
        }
        items.push({
          label: props.logoutTooltip,
          icon: "ri-logout-box-line",
          command: () => {
            // Will be handled by handleLogout method
            document.dispatchEvent(new CustomEvent("topbar-logout"));
          },
        });
      }

      return items;
    });

    return {
      isMobile,
      computedSettingsItems,
      doLogout,
    };
  },
  mounted() {
    // Listen for logout event from settings menu on mobile
    this._logoutHandler = () => this.handleLogout();
    document.addEventListener("topbar-logout", this._logoutHandler);
  },
  beforeUnmount() {
    document.removeEventListener("topbar-logout", this._logoutHandler);
  },
  methods: {
    getLabel(item) {
      if (item.labelKey) {
        return this.$t(item.labelKey);
      }
      return item.label || "";
    },
    hasActiveChild(item) {
      return sectionContainsActivePath(item, this.$route.path);
    },
    toggleDropdown(event, item) {
      const refName = "dropdown-" + (item.labelKey || item.label);
      const menuRef = this.$refs[refName];
      if (menuRef && menuRef[0]) {
        menuRef[0].toggle(event);
      }
    },
    getDropdownItems(item) {
      return item.items.map((subitem) => ({
        label: this.getLabel(subitem),
        icon: subitem.icon,
        command: () => {
          this.$router.push(subitem.to);
        },
      }));
    },
    toggleSettingsMenu(event) {
      this.$refs.settingsMenu.toggle(event);
    },
    async handleLogout() {
      this.$emit("logout");
      await this.doLogout(this.loginRouteName);
    },
  },
};
</script>

<style lang="scss" scoped>
.layout-topbar {
  position: fixed;
  top: 0;
  height: var(--topbar-height, 4rem);
  background-color: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  z-index: 997;
  transition:
    left 0.3s ease,
    width 0.3s ease,
    background-color 0.3s ease;
}

.topbar-start {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
  overflow: hidden;
}

.topbar-menu-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background-color: transparent;
  border-radius: var(--p-border-radius);
  color: var(--p-text-color);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--p-content-hover-background);
  }

  i {
    font-size: 1.5rem;
  }
}

.topbar-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  border: none;
  outline: none;

  &:focus {
    outline: none;
  }
}

.topbar-logo-image {
  height: 2.5rem;
  width: auto;
  border: none;
  outline: none;
}

.topbar-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
}

.topbar-menu-dropdown {
  position: relative;
}

.topbar-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  text-decoration: none;
  color: var(--p-text-color);
  border: none;
  background-color: transparent;
  border-radius: var(--p-border-radius);
  transition:
    background-color 0.2s,
    color 0.2s;
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background-color: var(--p-content-hover-background);
  }

  &.active {
    color: var(--p-primary-color);
    background-color: var(--p-highlight-background);
  }

  i {
    font-size: 1.25rem;
  }

  .dropdown-arrow {
    font-size: 1rem;
    margin-left: 0.25rem;
  }
}

.topbar-end {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.topbar-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background-color: transparent;
  border-radius: 50%;
  color: var(--p-text-color);
  cursor: pointer;
  transition: background-color 0.2s;
  text-decoration: none;

  &:hover {
    background-color: var(--p-content-hover-background);
  }

  i {
    font-size: 1.25rem;
  }
}
</style>
